/// <reference path="../../../typings/browser.d.ts" />

namespace Application.Controllers {
    'use strict';

    import IPortMapping = Models.IPortMapping;
    import PortMapping = Models.PortMapping;

    export interface INATModalScope extends ng.IScope {
        gateway: Models.Gateway;
        portMappings: PortMapping[];
        ruleMode: boolean;
        natConfigModel: IPortMapping;
        nicElementsMap: {[id: number]: INicElementPair};
        sortedNicElements: INicElementPair[];
        createSaveRule: () => void;
        deleteRule: (mapping: PortMapping) => void;
        editRule: (mapping: PortMapping) => void;
        toggleRuleMode: (mapping?: PortMapping) => void;
        modalClose: () => void;
        modalSave: () => void;

        getNicElementLabel: (pair: INicElementPair) => string;
    }

    interface INicElementPair {
        nic: Models.NetworkInterface;
        element: Models.NetworkElement;
    }

    /**
     * NATModalController class
     */
    export class NATModalController {
        public static templateUrl: string = "decorators/modals/nat-modal.html";
        public static ID: string = "#id-nat-modal";
        public static $name: string = "natModalController";
        public static $inject: string[] = ['$scope',
                                           'ModalService',
                                           'gatewayService',
                                           'networkElementService',
                                           'messageTranslationsService',
                                           'close',
                                           '$timeout',
                                           'gateway'];

        private $scope: INATModalScope;
        private nextRuleNumber: number;
        private modalService: ngModal.IModalService;
        private gatewayService: Services.GatewayService;
        private networkElementService: Services.NetworkElementService;
        private messageTranslationsService: Services.MessageTranslationsService;
        private close: (parameters: ngModal.ICloseParameters) => void;
        private initialConfig: Models.IGateway;
        private indexOfEdit: number;

        /**
         * Constructor
         */
        constructor($scope: INATModalScope,
                    modalService: ngModal.IModalService,
                    gatewayService: Services.GatewayService,
                    networkElementService: Services.NetworkElementService,
                    messageTranslationsService: Services.MessageTranslationsService,
                    close: (parameters: ngModal.ICloseParameters) => void,
                    $timeout: ng.ITimeoutService,
                    gateway: Models.Gateway) {

            $timeout(() => {
                window.altair_admin_common_init();
                window.altair_forms_validation();
            }, 500);

            this.$scope = $scope;
            this.$scope.ruleMode = false;
            this.$scope.gateway = gateway;
            this.$scope.natConfigModel = new Models.PortMapping();
            this.$scope.natConfigModel.protocol = "TCP";
            this.modalService = modalService;
            this.gatewayService = gatewayService;
            this.networkElementService = networkElementService;
            this.messageTranslationsService = messageTranslationsService;
            this.close = close;
            this.initialConfig = Common.Utils.clone(this.$scope.gateway) as Models.IGateway;
            this.indexOfEdit = -1;

            this.$scope.toggleRuleMode = (mapping?: PortMapping): void => {
                this.$scope.ruleMode = !this.$scope.ruleMode;
                if (this.$scope.ruleMode) {
                    if (!mapping) {
                        // Creating a new rule
                        this.indexOfEdit = -1;
                        this.$scope.natConfigModel = new PortMapping();
                        this.$scope.natConfigModel.protocol = "TCP";
                        this.$scope.natConfigModel.ruleNumber = this.nextRuleNumber;
                    } else {
                        // Populate the rule to be edited
                        this.indexOfEdit = this.$scope.portMappings.indexOf(mapping);
                        this.$scope.natConfigModel = PortMapping.copy(mapping);
                        if (Common.Utils.exists(this.$scope.natConfigModel.originalSourceNic)) {
                            this.$scope.natConfigModel.originalSourceNic =
                                this.$scope.nicElementsMap[this.$scope.natConfigModel.originalSourceNic.id].nic;
                        }
                        if (Common.Utils.exists(this.$scope.natConfigModel.originalDestinationNic)) {
                            this.$scope.natConfigModel.originalDestinationNic =
                                this.$scope.nicElementsMap[this.$scope.natConfigModel.originalDestinationNic.id].nic;
                        }
                        if (Common.Utils.exists(this.$scope.natConfigModel.translatedSourceNic)) {
                            this.$scope.natConfigModel.translatedSourceNic =
                                this.$scope.nicElementsMap[this.$scope.natConfigModel.translatedSourceNic.id].nic;
                        }
                        if (Common.Utils.exists(this.$scope.natConfigModel.translatedDestinationNic)) {
                            this.$scope.natConfigModel.translatedDestinationNic =
                                this.$scope.nicElementsMap[this.$scope.natConfigModel.translatedDestinationNic.id].nic;
                        }
                    }
                }
            };

            this.$scope.modalClose = (): void => {
                this.close(undefined);
            };

            this.$scope.modalSave = (): void => {
                this.gatewayService.setPortMappingsByGateway(this.$scope.gateway, this.$scope.portMappings).then(() => {
                    this.$scope.modalClose();
                }).catch((reason: ngCustom.IReason) => {
                    this.messageTranslationsService.showPromiseError(reason);
                });
            };

            this.$scope.createSaveRule = (): void => {
                this.$scope.natConfigModel.originalDestinationPorts = this.$scope.natConfigModel.originalDestinationPorts.replace(' ', '');
                this.$scope.natConfigModel.translatedDestinationPorts =
                    this.$scope.natConfigModel.translatedDestinationPorts.replace(' ', '');
                // sanity check
                if (this.$scope.natConfigModel.ruleNumber === undefined ||
                    (this.$scope.natConfigModel.originalDestinationPorts !== '' &&
                     !Common.Utils.validatePortList(this.$scope.natConfigModel.originalDestinationPorts)) ||
                    (this.$scope.natConfigModel.translatedDestinationPorts !== '' &&
                     !Common.Utils.validatePortList(this.$scope.natConfigModel.translatedDestinationPorts)))
                     return;

                // now checks that the number of ports matches
                let origDestPorts: number[] = Common.Utils.parsePortList(this.$scope.natConfigModel.originalDestinationPorts);
                let transDestPorts: number[] = Common.Utils.parsePortList(this.$scope.natConfigModel.translatedDestinationPorts);

                if (origDestPorts === undefined || transDestPorts === undefined || origDestPorts.length !== transDestPorts.length) {
                    this.modalService.showModal({
                        templateUrl: Application.Controllers.InfoModalController.templateURL,
                        controller: Application.Controllers.InfoModalController.$name,
                        inputs: {
                            title: 'Incorrect port ranges',
                            description: "The number of original destination ports must be equal " +
                                "to the number of translated destination ports",
                        },
                    }).then((modal: ngModal.IModalObject) => {
                        let modalElem: UIkit.ModalElement = UIkit.modal(Application.Controllers.InfoModalController.ID,
                                                            {bgclose: false, modal: false, keyboard: false});
                        modalElem.show();
                        modal.close.then((result: Object) => {
                            modalElem.hide();
                        });
                    });
                    return;
                }

                // add or update rule in list
                if (this.indexOfEdit !== -1) {
                    this.$scope.portMappings[this.indexOfEdit] = this.$scope.natConfigModel;
                } else {
                    this.$scope.portMappings.push(this.$scope.natConfigModel);
                }
                this.calculateRuleNumber();
                this.$scope.toggleRuleMode();
            };

            this.$scope.editRule = (mapping: PortMapping): void => {
                this.$scope.toggleRuleMode(mapping);
            };

            this.$scope.deleteRule = (mapping: PortMapping): void => {
                let index: number = this.$scope.portMappings.indexOf(mapping);
                this.modalService.showModal({
                templateUrl: Application.Controllers.ConfirmModalController.templateURL,
                controller: Application.Controllers.ConfirmModalController.$name,
                inputs: {
                        title: 'DELETE RULE',
                        description: "The rule will be removed.",
                        name: '',
                        type: 'information',
                    },
                }).then((modal: ngModal.IModalObject) => {
                    let modalElem: UIkit.ModalElement = UIkit.modal(Application.Controllers.ConfirmModalController.ID,
                                                        {bgclose: false, modal: false, keyboard: false});
                    modalElem.show();
                    modal.close.then((result: Object) => {
                        modalElem.hide();
                        if (result) {
                            this.$scope.portMappings.splice(index, 1);
                            this.calculateRuleNumber();
                        }
                    });
                });
            };

            this.$scope.getNicElementLabel = (pair: INicElementPair): string => {
                return pair.element.name + " (" + pair.nic.name + " " + pair.nic.ipAddress + ")";
            };

            this.gatewayService.getPortMappingsByGateway(this.$scope.gateway).then((portMappings: Models.PortMapping[]) => {
                this.$scope.portMappings = portMappings;
                this.calculateRuleNumber();

                this.$scope.nicElementsMap = {};
                this.$scope.sortedNicElements = [];
                let networkIds: number[] = [];
                this.$scope.gateway.networkInterfaces.forEach((nic: Models.NetworkInterface) => {
                    if (Common.Utils.exists(nic.networkConnectedTo) && networkIds.indexOf(nic.networkConnectedTo.id) === -1) {
                        networkIds.push(nic.networkConnectedTo.id);
                    }

                    // add the network interfaces of the gateway itself (required for those not connected to a network)
                    this.$scope.nicElementsMap[nic.id] = {
                        nic: nic,
                        element: gateway,
                    };
                });

                // add the network interfaces of the elements connected to the networks where the gateway is connected
                networkIds.forEach((id: number) => {
                    this.networkElementService.findConnectedToNetworkId(id)
                    .then((elements: Models.INetworkElement[]) => {
                        elements.forEach((element: Models.INetworkElement) => {
                            element.networkInterfaces.forEach((nic: Models.NetworkInterface) => {
                                if (Common.Utils.exists(nic.networkConnectedTo) && networkIds.indexOf(nic.networkConnectedTo.id) !== -1) {
                                    this.$scope.nicElementsMap[nic.id] = {
                                        nic: nic,
                                        element: element,
                                    };
                                }
                            });
                        });
                        Object.keys(this.$scope.nicElementsMap).forEach((key: string) => {
                            if (!this.$scope.sortedNicElements.some( nicElement => nicElement.nic.id === Number(key))) {
                                this.$scope.sortedNicElements.push(this.$scope.nicElementsMap[key]);
                            }
                        });
                        this.$scope.sortedNicElements.sort((a: INicElementPair, b: INicElementPair): number => {
                            return a.element.name.localeCompare(b.element.name);
                        });
                    });
                });
            });
        }

        private calculateRuleNumber(): void {
            this.nextRuleNumber = 1;
            this.$scope.portMappings.forEach((mapping: PortMapping) => {
                if (mapping.ruleNumber >= this.nextRuleNumber) {
                    this.nextRuleNumber = mapping.ruleNumber + 1;
                }
            });
        }
    }
}
